import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { OnboardingProvider } from '@/providers/onboarding-provider'
import { useOnboarding } from '@/hooks/use-onboarding'
import { OnboardingSidebar } from './-components/onboarding-sidebar'
import { AnimatedOutlet } from './-components/animated-outlet'

export const Route = createFileRoute('/onboarding')({
  component: OnboardingLayout,
})

// Routes that are handoff pages, not onboarding steps. They shouldn't render
// the step sidebar (which can briefly flash the wrong step list while state
// hydrates, e.g. on verification-link activation from an email client).
const HANDOFF_ROUTES = new Set(['/onboarding/activated', '/onboarding/join', '/onboarding/partner-invite'])

function OnboardingLayout() {
  return (
    <OnboardingProvider>
      <OnboardingLayoutInner />
    </OnboardingProvider>
  )
}

// Post-/email steps each advance currentStep based only on their own local
// input, never verifying that the /email step was actually completed. Combined
// with sessionStorage being cleared on successful submission (or tab-scoped
// absence on a brand-new tab), this lets the user reach /complete with an
// empty email/password and hit the raw Pydantic 422. This guard is the single
// place those prerequisites get asserted.
const STEPS_REQUIRING_CREDENTIALS = new Set<string>([
  '/onboarding/company-territory',
  '/onboarding/territory',
  '/onboarding/team',
  '/onboarding/partners',
  '/onboarding/claim-territory',
])

function OnboardingLayoutInner() {
  const { email, password, onboardingMode, inviteToken } = useOnboarding()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const isMember = onboardingMode === 'member'

    // Member flow needs inviteToken everywhere. It survives page refreshes via
    // sessionStorage now, but not cross-tab or if cleared — route back through
    // /join which shows a "No invite token found" error so she re-clicks the
    // email link.
    if (isMember && !inviteToken && location.pathname !== '/onboarding/join') {
      navigate({ to: '/onboarding/join' })
      return
    }

    // Both flows past /email need password in memory. Missing it means /email
    // wasn't completed in this session — bounce back so she re-enters it
    // (other fields rehydrate from sessionStorage). Founder also needs email.
    if (STEPS_REQUIRING_CREDENTIALS.has(location.pathname)) {
      if (isMember && !password) {
        navigate({ to: '/onboarding/email' })
        return
      }
      if (!isMember && (!password || !email)) {
        navigate({ to: '/onboarding/email' })
      }
    }
  }, [location.pathname, email, password, onboardingMode, inviteToken, navigate])

  if (HANDOFF_ROUTES.has(location.pathname)) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <AnimatedOutlet />
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-background">
      <OnboardingSidebar />
      <main className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl flex-1">
          <AnimatedOutlet />
        </div>
      </main>
    </div>
  )
}
